import { ErrorCode, type ApiResponse, type AuthSession } from '@tutor/shared';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * The access token is held in memory only — never localStorage or a readable cookie.
 * It dies with the tab, and the httpOnly refresh cookie is what survives a reload.
 */
let accessToken: string | null = null;

/** Called whenever a refresh produces a new session, so the auth store can follow. */
type SessionListener = (session: AuthSession | null) => void;
let onSessionChange: SessionListener = () => {};

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setSessionListener(listener: SessionListener): void {
  onSessionChange = listener;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, string[]>;
  readonly requestId?: string;

  constructor(status: number, body: { code: string; message: string; details?: Record<string, string[]>; requestId?: string }) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.requestId = body.requestId;
  }

  /** True when the caller is authenticated but not allowed — the 403 family. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isValidation(): boolean {
    return this.code === ErrorCode.VALIDATION_FAILED;
  }

  /** First message for a field, for wiring server errors back into a form. */
  fieldError(field: string): string | undefined {
    return this.details?.[field]?.[0];
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Set for the refresh call itself, to stop it recursing into a refresh. */
  skipAuthRefresh?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }

  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * De-duplicates concurrent refreshes.
 *
 * A dashboard fires several queries at once; when the access token expires they would
 * all 401 together. Without this, each would rotate the refresh token in parallel and
 * the losers would present an already-consumed token — which the API correctly treats
 * as theft and responds to by killing the whole session.
 */
let refreshInFlight: Promise<AuthSession | null> | null = null;

async function refreshSession(): Promise<AuthSession | null> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(buildUrl('/api/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) return null;

      const payload = (await response.json()) as ApiResponse<AuthSession>;
      if (!payload.success) return null;

      accessToken = payload.data.accessToken;
      onSessionChange(payload.data);
      return payload.data;
    } catch {
      return null;
    } finally {
      // Cleared on the next tick so callers awaiting this promise all see the result.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!text) return undefined as T;

  let payload: ApiResponse<T>;
  try {
    payload = JSON.parse(text) as ApiResponse<T>;
  } catch {
    throw new ApiError(response.status, {
      code: ErrorCode.INTERNAL_ERROR,
      message: 'The server returned an unreadable response',
    });
  }

  if (!response.ok || payload.success === false) {
    const error = 'error' in payload ? payload.error : undefined;
    throw new ApiError(response.status, {
      code: error?.code ?? ErrorCode.INTERNAL_ERROR,
      message: error?.message ?? 'Something went wrong',
      details: error?.details,
      requestId: error?.requestId,
    });
  }

  return payload.data;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, skipAuthRefresh, headers, ...init } = options;

  const send = (): Promise<Response> =>
    fetch(buildUrl(path, query), {
      ...init,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

  let response = await send();

  // One transparent retry after a refresh. If the refresh also fails the session is
  // genuinely gone and the 401 propagates to the router, which sends the user to /login.
  if (response.status === 401 && !skipAuthRefresh) {
    const session = await refreshSession();
    if (session) {
      response = await send();
    } else {
      accessToken = null;
      onSessionChange(null);
    }
  }

  return parse<T>(response);
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
};
